import React, { ChangeEvent, useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import "../../../styles/Login.scss";

interface HowToCategory {
    id: number;
    title: string;
    slug: string;
    header: string | null;
}

interface HowToCategoryModalProps {
    category: HowToCategory;
    onClose: () => void;
    onSaved: () => void;
}

const HowToCategoryModal: React.FC<HowToCategoryModalProps> = ({
                                                                   category,
                                                                   onClose,
                                                                   onSaved,
                                                               }) => {
    const [newImage, setNewImage] =
        useState<File | null>(null);

    const [imagePreview, setImagePreview] = useState(
        category.header
            ? `${IMAGE_URL}/${category.header}`
            : ""
    );

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        setNewImage(null);

        setImagePreview(
            category.header
                ? `${IMAGE_URL}/${category.header}`
                : ""
        );

        setError("");
        setSuccess("");
    }, [category]);

    const handleImageChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0] ?? null;

        setNewImage(file);

        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            setImagePreview(
                category.header
                    ? `${IMAGE_URL}/${category.header}`
                    : ""
            );
        }
    };

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (!newImage) {
            setError("Please select a new header image.");
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();

            formData.append("image", newImage);
            formData.append("_method", "PUT");

            const response = await api.post(
                `/admin/how-to-categories/${category.id}`,
                formData,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data",
                    },
                }
            );



            setSuccess(
                "How To category header updated successfully."
            );

            onSaved();
        } catch (err: any) {
            console.error(
                "Failed to update How To category:",
                err
            );

            if (err.response?.data?.errors) {
                const validationErrors =
                    err.response.data.errors;

                const firstError =
                    Object.values(validationErrors)[0];

                if (Array.isArray(firstError)) {
                    setError(String(firstError[0]));
                } else {
                    setError(String(firstError));
                }
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to update How To category header."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal admin-how-to-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="admin-modal-header">
                    <h2>
                        Edit {category.title} Header
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="form-success">
                            {success}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="how-to-category-header">
                            Header Image
                        </label>

                        {imagePreview && (
                            <div className="admin-image-preview">
                                <img
                                    src={imagePreview}
                                    alt={`${category.title} Header`}
                                />
                            </div>
                        )}

                        <input
                            id="how-to-category-header"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            disabled={loading}
                        />
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="admin-primary-button"
                            onClick={handleSave}
                            disabled={
                                loading || !newImage
                            }
                        >
                            {loading
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HowToCategoryModal;
